import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Wind, Compass } from "lucide-react";
import { fetchFarm, fetchThresholds } from "@/lib/api";
import { sprayBadge, fmt, RISK_COLORS, deltaTLevel } from "@/lib/risk";
import StatusBadge from "@/components/StatusBadge";

export default function SprayPage() {
    const { data } = useQuery({ queryKey: ["farm"], queryFn: fetchFarm, refetchInterval: 60_000 });
    const { data: thr } = useQuery({ queryKey: ["thresholds"], queryFn: fetchThresholds });
    if (!data || !thr) return <div className="h-40 bg-white rounded-2xl border animate-pulse" />;

    const suitable = data.stations.filter((s) => s.spray === "suitable");
    const marginal = data.stations.filter((s) => s.spray === "marginal");
    const not = data.stations.filter((s) => s.spray === "not_suitable");

    return (
        <div className="space-y-6" data-testid="spray-page">
            <div>
                <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Spray Conditions</h1>
                <p className="text-sm text-slate-500 mt-1">Where can I spray right now?</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <SprayBucket level="green" label="Suitable" stations={suitable} />
                <SprayBucket level="yellow" label="Marginal" stations={marginal} />
                <SprayBucket level="red" label="Not suitable" stations={not} />
            </div>

            <div className="fw-card overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                            <th className="px-4 py-3">Station</th>
                            <th className="px-4 py-3">Wind</th>
                            <th className="px-4 py-3">Direction</th>
                            <th className="px-4 py-3">Gust</th>
                            <th className="px-4 py-3">Temp</th>
                            <th className="px-4 py-3">RH</th>
                            <th className="px-4 py-3">Delta T</th>
                            <th className="px-4 py-3">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.stations.map((s) => {
                            const b = sprayBadge(s.spray);
                            const dtL = deltaTLevel(s.current.delta_t, thr.delta_t);
                            return (
                                <tr key={s.id} data-testid={`spray-row-${s.id}`}
                                    className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50 cursor-pointer"
                                    onClick={() => window.location.assign(`/stations/${s.id}`)}>
                                    <td className="px-4 py-3">
                                        <Link to={`/stations/${s.id}`} className="font-semibold text-slate-900 hover:underline">{s.name}</Link>
                                    </td>
                                    <td className="px-4 py-3 fw-metric font-semibold text-slate-800">{fmt(s.current.wind, 1)} km/h</td>
                                    <td className="px-4 py-3"><span className="inline-flex items-center gap-1.5"><Compass className="h-3 w-3 text-slate-400" />{s.current.wind_dir}</span></td>
                                    <td className="px-4 py-3 fw-metric font-semibold text-slate-800">{fmt(s.current.gust, 1)} km/h</td>
                                    <td className="px-4 py-3 fw-metric font-semibold text-slate-800">{fmt(s.current.temp, 1)}°C</td>
                                    <td className="px-4 py-3 fw-metric font-semibold text-slate-800">{fmt(s.current.humidity, 0)}%</td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <span className="fw-metric font-semibold text-slate-800">{fmt(s.current.delta_t, 1)}°C</span>
                                            <StatusBadge level={dtL} size="sm" />
                                        </div>
                                    </td>
                                    <td className="px-4 py-3"><StatusBadge level={b.level} label={b.label} /></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function SprayBucket({ level, label, stations }) {
    const c = RISK_COLORS[level];
    return (
        <div className="fw-card p-5" data-testid={`spray-bucket-${level}`} style={{ borderColor: c.border, background: c.bg }}>
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: c.text }}>{label}</div>
                    <div className="fw-metric text-4xl font-extrabold text-slate-900 mt-1">{stations.length}</div>
                </div>
                <Wind className="h-6 w-6" style={{ color: c.hex }} />
            </div>
            <div className="mt-3 space-y-1">
                {stations.length === 0 ? (
                    <div className="text-xs text-slate-600">No stations</div>
                ) : (
                    stations.map((s) => (
                        <Link key={s.id} to={`/stations/${s.id}`}
                            className="flex items-center justify-between px-2 py-1.5 rounded-md bg-white/70 hover:bg-white text-sm">
                            <span className="font-semibold text-slate-900">{s.name}</span>
                            <span className="text-xs text-slate-600 fw-metric">ΔT {fmt(s.current.delta_t, 1)}°</span>
                        </Link>
                    ))
                )}
            </div>
        </div>
    );
}
